import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { GIButton, Modal } from 'app-components';

import { useStateCode } from '../../../hooks/useStateCode';
import { ModalContextType } from '../../../contexts/modalContext';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import { IDisenrollGroup } from '../types/customGroupingTypes';
import { useAppDispatch, useAppSelector, useModal } from '../../../hooks';
import { ENROLLMENT_ACTIONS, FROM_TAB } from './modalTypes';
import useCustomGrouping from '../hooks/useCustomGrouping';
import CapitalizedButton from '../components/common/CapitalizedButton';

enum PLAN_TYPE {
    HEALTH = 'HEALTH',
    DENTAL = 'DENTAL',
    BOTH = 'BOTH',
}

interface IModalProps extends ModalContextType {
    typeOfPlan: PLAN_TYPE;
    enrollmentAction: ENROLLMENT_ACTIONS;
    isAptcWarningVisible: boolean;
    group: IDisenrollGroup;
    dentalDetails?: IDisenrollGroup;
    fromTab: FROM_TAB;
    disenrollmentState: {
        reason: string;
        reasonOther: string;
    };
}

export default function DisenrollConfirmationModal({
    typeOfPlan,
    enrollmentAction,
    isAptcWarningVisible,
    closeAllModals,
    closeModal,
    group,
    dentalDetails,
    disenrollmentState,
    fromTab,
}: IModalProps) {
    const intl = useIntl();
    const { openModal } = useModal();
    const { isMemberLevelDisenrollEnabled } = useAppSelector(state => state.common.environment.flags);
    const { isReportLifeEventVisible, areCancelContinueControlsVisible, isExemptionsUrlVisible } = useStateCode();
    const dispatch = useAppDispatch();
    const { submitDisenrollment } = useCustomGrouping({ dispatch });

    function onClose(e) {
        e?.preventDefault();
        if (fromTab === FROM_TAB.PLANSUMMARY) {
            closeAllModals();
        } else {
            closeModal('disenrollmentConfirmation');
        }
    }

    async function onContinue() {
        let actualDates = {
            actualDatesEndOfCurrentMonth: '',
            actualDatesEndOfNextMonth: '',
            actualDatesEndOfMonthAfterNext: '',
        };

        let healthDetails;

        // Determine health and dental details based on the group and plan type
        if (group) {
            if ([PLAN_TYPE.HEALTH, PLAN_TYPE.BOTH].includes(typeOfPlan)) {
                healthDetails = group;
            } else if (typeOfPlan === PLAN_TYPE.DENTAL) {
                dentalDetails = group;
            }
        }

        // Handle effectuated plan
        if (group.isPlanEffectuated === 'Y') {
            actualDates = {
                actualDatesEndOfCurrentMonth: group.endOfCurrentMonth,
                actualDatesEndOfNextMonth: group.endOfNextMonth,
                actualDatesEndOfMonthAfterNext: group.endOfMonthAfterNext,
            };

            openModal('disenrollmentFlow', {
                typeOfPlan,
                ...actualDates,
                disenrollGroup: group,
                dentalDetails,
                healthDetails,
                disenrollmentState,
                enrollmentAction,
                fromTab,
            });
            return;
        }

        // Handle non-effectuated plan
        try {
            const response = await submitDisenrollment({
                params: {
                    typeOfPlan,
                    healthDetails,
                    dentalDetails,
                    disenrollGroup: group,
                    disenrollOption: disenrollmentState,
                    isMemberLevelDisenrollEnabled,
                },
            });

            if (response.data === 'success') {
                closeAllModals();
                openModal('submitDisenrollModal', { enrollmentAction, fromTab });
            } else {
                openModal('disenrollFailureModal');
            }
        } catch (e) {
            openModal('disenrollFailureModal');
        }
    }

    const exemptionsUrl = intl.formatMessage({ id: 'indportal.plansummary.exemptionsUrl' });

    function getHeader(enrollmentActionType: 'submit' | 'cancel', planType: PLAN_TYPE) {
        const headers = {
            HEALTH: `indportal.plansummary.${enrollmentActionType}.doyouwanttocontinue.health`,
            DENTAL: `indportal.plansummary.${enrollmentActionType}.doyouwanttocontinue.dental`,
            BOTH: `indportal.plansummary.${enrollmentActionType}.doyouwanttocontinueboth`,
        };
        return headers[planType];
    }

    return (
        <Modal id="disenrollConfirmation">
            <Modal.Header
                id="disenrollConfirmationHeader"
                titleId={getHeader(enrollmentAction, typeOfPlan)}
                onHide={onClose}
            />
            <Modal.Body id="disenrollConfirmationBody">
                <ConditionalRendering isTrue={typeOfPlan === PLAN_TYPE.HEALTH}>
                    <p>
                        <FormattedMessage id="indportal.plansummary.youhaveseelctedtodisenroll" />
                    </p>
                </ConditionalRendering>

                <ConditionalRendering isTrue={typeOfPlan === PLAN_TYPE.DENTAL}>
                    <ConditionalRendering isTrue={isReportLifeEventVisible}>
                        <p>
                            <FormattedMessage id="indportal.plansummary.youhaveseelctedtodisenroll" />
                        </p>
                        <p>
                            <FormattedMessage id="indportal.plansummary.ifyoucanaffordbut" />
                        </p>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={!isReportLifeEventVisible}>
                        <p>
                            <FormattedMessage id="indportal.plansummary.youhaveseelctedtodisenrolldental" />
                        </p>
                        <p>
                            <FormattedMessage id="indportal.plansummary.youhaveseelctedtodisenroll2dental" />
                        </p>
                    </ConditionalRendering>
                </ConditionalRendering>

                <ConditionalRendering isTrue={typeOfPlan === PLAN_TYPE.BOTH && isReportLifeEventVisible}>
                    <p>
                        <FormattedMessage id="indportal.plansummary.youhaveseelctedtodisenroll" />
                    </p>
                </ConditionalRendering>
                <ConditionalRendering isTrue={typeOfPlan === PLAN_TYPE.BOTH && !isReportLifeEventVisible}>
                    <p>
                        <FormattedMessage id="indportal.plansummary.youhaveseelctedtodisenrollboth" />
                    </p>
                    <p>
                        <FormattedMessage id="indportal.plansummary.youhaveseelctedtodisenroll2both" />
                    </p>
                    <p>
                        <FormattedMessage id="indportal.plansummary.youhaveseelctedtodisenroll3both" />
                    </p>
                </ConditionalRendering>

                <ConditionalRendering isTrue={typeOfPlan === PLAN_TYPE.HEALTH && isAptcWarningVisible}>
                    <p>
                        <FormattedMessage id="indportal.plansummary.aptcwarning" />
                    </p>
                </ConditionalRendering>

                <ConditionalRendering isTrue={[PLAN_TYPE.HEALTH, PLAN_TYPE.BOTH].includes(typeOfPlan)}>
                    <div className="margin20-t usa-alert usa-alert--info gi-alert gi-alert__info margin0-b">
                        <p>
                            <FormattedMessage id="indportal.plansummary.ifyoucanaffordbut" />
                        </p>
                        <ConditionalRendering isTrue={isExemptionsUrlVisible}>
                            <p>
                                <FormattedMessage id="indportal.plansummary.thefeecontent4" />{' '}
                                <a href={exemptionsUrl} target="_blank">
                                    <FormattedMessage id="indportal.plansummary.exemptionsUrl" />
                                </a>
                            </p>
                        </ConditionalRendering>
                    </div>
                </ConditionalRendering>
            </Modal.Body>
            <Modal.Controls>
                <ConditionalRendering isTrue={!areCancelContinueControlsVisible}>
                    <CapitalizedButton
                        id="disenrollConfirmationModalNoButton"
                        onClickHandler={onClose}
                        variant="secondary"
                        labelId="button.no"
                    />
                    <CapitalizedButton
                        id="disenrollConfirmationModalYesButton"
                        onClickHandler={onContinue}
                        variant="primary"
                        labelId="button.yes"
                        leftMargin={true}
                    />
                </ConditionalRendering>

                <ConditionalRendering isTrue={areCancelContinueControlsVisible}>
                    <CapitalizedButton
                        id="disenrollConfirmationModalCancelButton"
                        onClickHandler={onClose}
                        variant="secondary"
                        labelId="button.cancel"
                    />
                    <CapitalizedButton
                        id="disenrollConfirmationModalContinueButton"
                        onClickHandler={onContinue}
                        variant="primary"
                        labelId="button.continue"
                        leftMargin={true}
                    />
                </ConditionalRendering>
            </Modal.Controls>
        </Modal>
    );
}
